"use client"

import React, { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import axios from "axios"
import { toast } from "react-toastify"
import "react-toastify/dist/ReactToastify.css"

import HeadingCp from "../components/HeadingCp"
//codex-- Use matching toolbar icons with the screen's existing estimate/search actions.
import { ArrowPathIcon, MagnifyingGlassIcon, PlusIcon } from "@heroicons/react/24/outline"
import DateFormat from "../components/DateFormat"
import LoadingSpinner from "../components/LoadingSpinner"
import SubMenu from "../components/SubMenu"

const ElectricalEstimateList = () => {
  const apiUrl = process.env.REACT_APP_API_URL
  const navigate = useNavigate()

  const [loading, setLoading] = useState(false)
  const [searchTerm, setSearchTerm] = useState("")
  const [estimates, setEstimates] = useState([])
  const [filteredEstimates, setFilteredEstimates] = useState([])

  useEffect(() => {
    fetchEstimates()
  }, [])

  const fetchEstimates = async () => {
    setLoading(true)
    try {
      const response = await axios.get(`${apiUrl}/electrical_estimate_crud.php`, {
        params: { listEstimates: 1 },
      })

      const data = Array.isArray(response.data) ? response.data : []
      setEstimates(data)
      setFilteredEstimates(data)
    } catch (error) {
      if (error.response?.status === 401) {
        navigate("/login")
      } else if (error.response?.status === 403) {
        toast.error(
          "Oops!! you don't have access to this area. Speak to the CFO.",
          { position: "top-center" }
        )
      } else {
        console.error("Error fetching electrical estimates:", error)
        toast.error("Error loading electrical estimates.", {
          position: "top-center",
        })
      }
    } finally {
      setLoading(false)
    }
  }

  // Search: project_address, contact_name, employee_name, id
  const handleSearchChange = (event) => {
    const value = event.target.value
    setSearchTerm(value)

    if (value === "") {
      setFilteredEstimates(estimates)
    } else {
      const lower = value.toLowerCase()
      const filtered = estimates.filter((item) => {
        return (
          (item.project_address &&
            item.project_address.toLowerCase().includes(lower)) ||
          (item.contact_name &&
            item.contact_name.toLowerCase().includes(lower)) ||
          (item.employee_name &&
            item.employee_name.toLowerCase().includes(lower)) ||
          String(item.id).includes(lower)
        )
      })
      setFilteredEstimates(filtered)
    }
  }

  const handleSearchClick = () => {
    handleSearchChange({ target: { value: searchTerm } })
  }

  const getLinksForEstimate = (item) => [
    {
      name: "Open Estimator",
      click: () => navigate(`/electrical_estimate/${item.id}`),
    },
  ]

  return (
    <div className="mx-auto max-w-7xl">
      {/* Heading */}
      <div className="flex items-center justify-between">
        <div className="mt-2 mb-4 py-6">
          <HeadingCp label="Electrical Estimate List" />
        </div>
      </div>

      {/*codex-- Match the full-width toolbar card, grouped actions, and lighter original button colors. */}
      <section aria-label="Electrical estimate tools" className="mb-6 w-full overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="flex flex-col gap-3 border-b border-slate-200 bg-slate-50/80 px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:px-5">
          <p className="text-sm font-semibold text-slate-800">Electrical estimate tools</p>
          <button type="button" onClick={() => navigate(`/electrical_estimate`)}
            className="inline-flex min-h-[40px] items-center justify-center gap-2 rounded-lg border border-blue-200 bg-blue-100 px-3 py-2 text-sm font-semibold text-blue-900 shadow-sm hover:bg-blue-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600">
            <PlusIcon aria-hidden="true" className="h-4 w-4" />New Estimate
          </button>
        </div>

        {/*codex-- Keep live filtering and native Enter search while separating buttons from the input. */}
        <form aria-label="Electrical estimate search" onSubmit={(event) => {
          event.preventDefault()
          handleSearchClick()
        }} className="flex flex-wrap items-end gap-3 p-4 sm:p-5">
          <div className="min-w-0 flex-[1_1_240px]">
            <label htmlFor="electrical-estimate-search" className="mb-1.5 block text-xs font-semibold text-slate-600">Search electrical estimates</label>
            <div className="relative">
              <MagnifyingGlassIcon aria-hidden="true" className="pointer-events-none absolute left-3 top-3.5 h-4 w-4 text-slate-400" />
              <input id="electrical-estimate-search" name="search" type="search" value={searchTerm}
                onChange={handleSearchChange} placeholder="Project, contact, estimator, or estimate ID"
                className="block h-11 w-full min-w-0 rounded-lg border border-slate-300 bg-white pl-9 pr-3 text-sm text-slate-900 shadow-sm focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20" />
            </div>
          </div>
          <div className="flex w-full gap-2 sm:w-auto">
            <button type="submit" disabled={!searchTerm}
              className="inline-flex min-h-[40px] flex-1 items-center justify-center rounded-lg border border-green-200 bg-green-100 px-3 py-2 text-sm font-semibold text-green-900 shadow-sm hover:bg-green-200 disabled:cursor-not-allowed disabled:opacity-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-green-600">Search</button>
            <button type="button" onClick={() => window.location.reload()}
              className="inline-flex min-h-[40px] flex-1 items-center justify-center gap-2 rounded-lg border border-orange-200 bg-orange-100 px-3 py-2 text-sm font-semibold text-orange-900 shadow-sm hover:bg-orange-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-600">
              <ArrowPathIcon aria-hidden="true" className="h-4 w-4" />Reset
            </button>
          </div>
        </form>
      </section>

      {/* Table */}
      <div className="tableContainer">
        {loading ? (
          <LoadingSpinner>Loading estimates…</LoadingSpinner>
        ) : filteredEstimates.length === 0 ? (
          <p className="text-sm text-slate-500">No electrical estimates found.</p>
        ) : (
          <table className="min-w-full divide-y divide-x divide-slate-300 rounded-lg border bg-slate-200">
            <thead>
              <tr className="divide-x divide-slate-400">
                <th className="px-3 py-3.5 text-left text-sm font-semibold text-slate-900">
                  ID
                </th>
                <th className="px-3 py-3.5 text-left text-sm font-semibold text-slate-900">
                  Date
                </th>
                <th className="px-3 py-3.5 text-left text-sm font-semibold text-slate-900">
                  Project
                </th>
                <th className="px-3 py-3.5 text-left text-sm font-semibold text-slate-900">
                  Contact
                </th>
                <th className="px-3 py-3.5 text-left text-sm font-semibold text-slate-900">
                  Estimator
                </th>
                <th className="px-3 py-3.5 text-left text-sm font-semibold text-slate-900">
                  Status
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 bg-white">
              {filteredEstimates.map((item, index) => (
                <tr
                  key={item.id}
                  className={`${index % 2 === 0 ? "bg-white" : "bg-slate-50"} divide-x`}
                >
                  <td className="py-4 pl-4 pr-3 text-sm font-medium text-slate-900">
                    <SubMenu
                      label={item.id}
                      idItem={item.id}
                      links={getLinksForEstimate(item)}
                    />
                  </td>

                  <td className="px-3 py-4 text-sm text-slate-600">
                    <DateFormat date={item.date} />
                  </td>

                  <td className="px-3 py-4 text-sm text-slate-800">
                    {item.project_address || "-"}
                  </td>

                  <td className="px-3 py-4 text-sm text-slate-600">
                    {item.contact_name || "-"}
                  </td>

                  <td className="px-3 py-4 text-sm text-slate-600">
                    {item.employee_name || "-"}
                  </td>

                  <td className="px-3 py-4 text-sm text-slate-600">
                    {item.status || "pending"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}

export default ElectricalEstimateList
